import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, 
  Grid, InputAdornment, InputLabel, MenuItem, Paper, Select, 
  TextField, Typography } from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

const UserDetailsForm = () => {
  const location = useLocation();
  const bookingData = location.state;
  const navigate = useNavigate();

  // Initialize passenger details with the seat information
  const [passengerDetails, setPassengerDetails] = useState(
    bookingData.selectedSeats.map(seat => ({
      seatData: seat,
      name: '',
      age: '',
      gender: 'Male',
      contact: '',
      email: '',
      address: '',
      acceptTerms: false,
    }))
  );

  const [couponCode, setCouponCode] = useState('');
  const [useAbhiCash, setUseAbhiCash] = useState(false);
  const [applyInsurance, setApplyInsurance] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    setPassengerDetails(prev => {
      const updatedDetails = [...prev];
      updatedDetails[index] = {
        ...updatedDetails[index],
        [name]: type === 'checkbox' ? checked : value,
      };
      return updatedDetails;
    });
  };

  const prepareBlockApiPayload = () => {
    return {
      "ResultIndex": bookingData.resultIndex || "1",
      "TraceId": bookingData.traceId,
      "BoardingPointId": bookingData.selectedBoardingPoint.CityPointIndex,
      "DroppingPointId": bookingData.selectedDroppingPoint.CityPointIndex,
      "Passenger": passengerDetails.map((passenger, index) => ({
        "LeadPassenger": index === 0,
        "PassengerId": index,
        "Title": passenger.gender === 'Male' ? 'Mr.' : 'Ms.',
        "Address": passenger.address || "Pune",
        "Age": parseInt(passenger.age) || 23,
        "Email": passenger.email,
        "FirstName": passenger.name.split(' ')[0],
        "Gender": passenger.gender === 'Male' ? 1 : 2,
        "IdNumber": null,
        "IdType": null,
        "LastName": passenger.name.split(' ').slice(1).join(' ') || " ",
        "Phoneno": passenger.contact,
        "Seat": passenger.seatData
      }))
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    const isValid = passengerDetails.every(
      passenger =>
        passenger.name &&
        passenger.age &&
        passenger.contact &&
        passenger.email &&
        passenger.acceptTerms
    );
  
    if (!isValid) {
      toast.error("Please fill all passenger details and accept the terms.");
      setIsSubmitting(false);
      return;
    }
  
    try {
      const payload = prepareBlockApiPayload();
      const jwtToken = sessionStorage.getItem("jwtToken");
  
      const response = await axios.post(
        'https://api.ipaisa.co.in/myapp/bus/block',
        payload,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
  
      console.log("API response:", response.data);
  
      // Handle the response structure
      if (response.data.status === "success") {
        const blockResult = response.data.data.BlockResult;
        
        if (blockResult.ResponseStatus === 1) {
          // Success case
          const finalBookingData = {
            ...bookingData,
            passengerDetails,
            blockResponse: response.data.data,
            couponCode,
            useAbhiCash,
            applyInsurance,
            currentUser: response.data.currentUser,
            userName: response.data.userName,
            userId: response.data.userId,
            totalFare: response.data.totalFare,
            walletBalance: response.data.walletBalance
          };
          
          toast.success("Seat Blocked Successfully", { 
            onClose: () => {
              navigate("/fareSummery", { state: finalBookingData });
            }
          });
        } else {
          // Error case
          const errorMessage = blockResult.Error?.ErrorMessage || "Failed to block seats";
          toast.error(errorMessage);
          
          if (errorMessage.includes("Lastname")) {
            // Handle specific last name error
            toast.info("Please provide both first and last name for all passengers");
          }
        }
      } else {
        toast.error(response.data.message || "Failed to process your request");
      }
    } catch (error) {
      console.error("Block API error:", error);
      if (error.response) {
        // The request was made and the server responded with a status code
        console.error("Error data:", error.response.data);
        console.error("Error status:", error.response.status);
        console.error("Error headers:", error.response.headers);
        toast.error(error.response.data.message || "An error occurred");
      } else if (error.request) {
        // The request was made but no response was received
        console.error("No response received:", error.request);
        toast.error("No response received from server");
      } else {
        // Something happened in setting up the request
        console.error("Error setting up request:", error.message);
        toast.error("Error setting up request");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if all required fields are filled and terms are accepted
  const isFormValid = passengerDetails.every(
    passenger => passenger.name && passenger.age && 
              passenger.contact && passenger.email && 
              passenger.acceptTerms
  );

  return (
    <Box sx={{ 
      backgroundColor: '#f8f8f8', 
      padding: '2rem', 
      borderRadius: '12px',
    }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Complete Your Booking
      </Typography>

      <Grid container spacing={4}>
        {/* Left Column - Passenger Details */}
        <Grid item xs={12} md={8}>
          {passengerDetails.map((passenger, index) => (
            <Paper key={index} sx={{ p: 3, mb: 3, borderRadius: '12px', boxShadow: 'none' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Passenger {index + 1} (Seat: {passenger.seatData.SeatName} - ₹{passenger.seatData.SeatFare})
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                {passenger.seatData.SeatType === 1 ? 'Seater' : 
                 passenger.seatData.SeatType === 2 ? 'Sleeper' : 
                 'Seat Cum Sleeper'} | 
                {passenger.seatData.IsUpper ? ' Upper Deck' : ' Lower Deck'} |
                {passenger.seatData.IsLadiesSeat ? ' Ladies Seat' : ''}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name (First and Last Name)"
                    name="name"
                    value={passenger.name}
                    onChange={(e) => handleInputChange(index, e)}
                    required
                    helperText="Please enter both first and last name"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Age"
                    name="age"
                    type="number"
                    value={passenger.age}
                    onChange={(e) => handleInputChange(index, e)}
                    required
                    inputProps={{ min: 1, max: 100 }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={passenger.gender}
                      onChange={(e) => handleInputChange(index, e)}
                      label="Gender"
                      required
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    name="contact"
                    type="tel"
                    value={passenger.contact}
                    onChange={(e) => handleInputChange(index, e)}
                    required
                    inputProps={{ maxLength: 10 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          +91
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={passenger.email}
                    onChange={(e) => handleInputChange(index, e)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={passenger.address}
                    onChange={(e) => handleInputChange(index, e)}
                  />
                </Grid>
              </Grid>

              {/* Terms and Conditions */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Checkbox
                  name="acceptTerms"
                  checked={passenger.acceptTerms}
                  onChange={(e) => handleInputChange(index, e)}
                  required
                />
                <Typography variant="body1">
                  Yes, I accept the Terms and conditions
                </Typography>
              </Box>
            </Paper>
          ))}
        </Grid>

        {/* Right Column - Fare Details */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: '12px', boxShadow: 'none' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Fare Details
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Grid container sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2">Total Fare</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">₹ {bookingData.totalAmount}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Total Amount To Be Paid
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    ₹ {bookingData.totalAmount}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* AbhiCash */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={useAbhiCash} 
                    onChange={(e) => setUseAbhiCash(e.target.checked)} 
                  />
                }
                label={
                  <Typography variant="body1">
                    <span style={{ fontWeight: 'bold' }}>AbhiCash</span> - Eligible: ₹25
                  </Typography>
                }
              />
            </Box>

            {/* Offers */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Offers
              </Typography>
              <FormControlLabel
                control={<Checkbox />}
                label={
                  <Typography variant="body1">
                    <span style={{ fontWeight: 'bold' }}>ABHICRICKET</span> - AbhiBus Cricket league: WIN a chance to watch LIVE cricket match
                  </Typography>
                }
              />
            </Box>

            {/* Coupon Code */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Got a coupon code?
              </Typography>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    placeholder="Enter coupon code"
                    size="small"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    sx={{ height: '40px' }}
                  >
                    APPLY
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Refund Policy */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Refund Policy
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2">Bus cancelled?</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>150% Refund</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Bad quality service?</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Upto 100% refund</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Bus delayed?</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Upto 100% refund</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Changed your plans?</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>100% refund</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Billing Address */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Billing address
              </Typography>
              <Typography variant="body2">
                Near Wakad Hinjewadi Bridge, Vijayanand Travels Parking, Opp Metro Station
                <br />
                Pune, Maharashtra, 411002
                <br />
                Contact: 7768006179, 9513935040
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Proceed to Pay'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="colored"
      />
    </Box>
  );
};

export default UserDetailsForm;